import { CellContext, SortingFn } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  familyProQuestionnaireFormUri,
  PaymentResponse,
  submissionStatusRank,
  TrackerActionType,
} from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import { Badge, ContextMenu, Tooltip } from "@smart/itops-sb-design-system-dom";
import {
  entriesOf,
  formatDateTime,
  isNotNullOrUndefined,
  jsonParseOrReturn,
  specialChars,
} from "@smart/itops-utils-basic";
import { MatterSubmission } from "@smart/manage-gql-client-dom";
import { useUser } from "@smart/manage-hooks-dom";

import { SubmissionStatusPill } from "../../../shared/submission-status-pill";
import { ActionsMenu, copySupportInformation } from "../../shared";
import { CellProps, MatterSpecificForm } from "../../types";

export type TabRowValue =
  | { submission: MatterSubmission; form: undefined }
  | { form: MatterSpecificForm; submission: undefined };

export const FormTitle = ({ row }: CellProps<TabRowValue>) => {
  const { submission } = row.original;
  const hasFailedPayment = useMemo(() => {
    const failedPaymentFields = submission?.formFields
      .filter((f) => f.values.type === "payment")
      .map((f) => {
        const response = jsonParseOrReturn(
          submission?.responses.find((r) => r.fieldUri === f.uri)?.value,
        ) as PaymentResponse | undefined;

        return (response?.charges &&
          entriesOf(response.charges).find(
            ([, { status }]) => status === "FAILED",
          ))?.[1];
      })
      .filter(isNotNullOrUndefined);

    const defaultErrorMessage =
      "An unexpected error occurred during transaction processing";
    return failedPaymentFields?.length
      ? failedPaymentFields.find(
          (charge) => charge.smokeballResponse?.failure?.message,
        )?.smokeballResponse?.failure?.message || defaultErrorMessage
      : undefined;
  }, [submission?.responses]);

  return (
    <div className="flex items-center">
      {submission?.formTitle || row.original.form?.title}
      {hasFailedPayment && (
        <Tooltip text={hasFailedPayment} type="error">
          <Badge
            className="w-fit p-5"
            label="Payment failed"
            theme="destructive"
            dataTestId="view-failed-payments"
          />
        </Tooltip>
      )}
    </div>
  );
};

export const Created = ({ row }: CellProps<TabRowValue>) => {
  const { dateDisplayFormat } = loadLocale();
  return row.original.submission?.createdAt
    ? formatDateTime(row.original.submission.createdAt, dateDisplayFormat)
    : specialChars.enDash;
};

export const Modified = ({ row }: CellProps<TabRowValue>) => {
  const { dateDisplayFormat } = loadLocale();
  if (row.original.form) return specialChars.enDash;

  const isOpened =
    row.original.submission?.rawSubmission.values?.communicationMethod ===
      "internalUse" ||
    !!row.original.submission?.trackers?.find(
      (t) => (t?.action as TrackerActionType) === "intake-form-opened",
    );
  const getContent = () => {
    if (row.original.submission?.formUri === familyProQuestionnaireFormUri) {
      return row.original.submission?.updatedAt
        ? formatDateTime(row.original.submission.updatedAt, dateDisplayFormat)
        : specialChars.enDash;
    }
    return isOpened
      ? formatDateTime(row.original.submission?.updatedAt, dateDisplayFormat)
      : "Not opened";
  };

  return <span data-testid="last-modified">{getContent()}</span>;
};

export const ContactName = ({ row }: CellProps<TabRowValue>) =>
  row.original.submission?.recipient?.contactName || specialChars.enDash;

export const statusSortingFn: SortingFn<TabRowValue> = (rowA, rowB) => {
  const rankA =
    submissionStatusRank[
      rowA.original.submission ? rowA.original.submission.status : "form"
    ];
  const rankB =
    submissionStatusRank[
      rowB.original.submission ? rowB.original.submission.status : "form"
    ];
  if (rankA > rankB) return 1;
  if (rankA < rankB) return -1;
  return 0;
};

export const Status = ({ row }: CellContext<TabRowValue, any>) => {
  const { submission } = row.original;
  return (
    <SubmissionStatusPill
      syncStatus={submission?.syncStatus}
      status={submission?.status}
      isForm={!!row.original.form}
      isInternalUse={
        submission?.status === "draft" &&
        submission.rawSubmission.values.communicationMethod === "internalUse"
      }
    />
  );
};

type SubmissionActionProps = Record<
  | "onView"
  | "onCancel"
  | "onDelete"
  | "onRemind"
  | "onEdit"
  | "onGenerateQRCode",
  (submission: MatterSubmission) => void
> &
  Record<"onViewForm", (form: MatterSpecificForm) => void>;

export const SubmissionAction =
  ({
    onView,
    onCancel,
    onDelete,
    onRemind,
    onEdit,
    onGenerateQRCode,
    onViewForm,
  }: SubmissionActionProps) =>
  ({ row }: CellProps<TabRowValue>) => {
    const { user } = useUser();

    if (row.original.form)
      return (
        <ContextMenu
          button={{
            className: "w-[2rem] h-[2rem]",
            variant: "secondarySubtle",
            leftIcon: "solidEllipsis",
          }}
          placement="bottom-end"
          offsetBy={5}
          items={[
            {
              label: "View",
              onClick: () => onViewForm(row.original.form!),
            },
            {
              label: "Copy support information",
              onClick: () => {
                copySupportInformation({
                  teamUri: user?.teamUri,
                  userUri: user?.userUri,
                  formUri: row.original.form!.uri,
                });
              },
              internal: true,
            },
          ]}
          id="list-options"
          className="w-fit"
        />
      );

    const { submission } = row.original;

    return (
      <ActionsMenu
        onView={onView}
        onEdit={onEdit}
        onRemind={onRemind}
        onCancel={onCancel}
        onDelete={onDelete}
        onGenerateQRCode={onGenerateQRCode}
        matterSubmission={submission}
        id="list-options"
        variant="subtle"
        className="w-fit"
        size="small"
      />
    );
  };
